// https://github.com/Mawi137/ngx-image-cropper/blob/master/src/image-cropper.component.ts
import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

interface MoveStart {
  active: boolean;
  type: string | null;
  position: string | null;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  clientX: number;
  clientY: number;
}

export interface Dimensions {
  width: number;
  height: number;
}

export interface CropperPosition {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

@Component({
  selector: 'app-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss']
})
export class ImageCropperComponent {
  private originalImage: any;
  private moveStart: MoveStart;
  private maxSize: Dimensions;
  private originalSize: Dimensions;

  cropper: CropperPosition;
  imgDataUrl: string;
  imageVisible = false;

  constructor(private elementRef: ElementRef) {
    this.reset();
  }

  @Input() boundingBox = { width: 840, height: 300 };
  @Input() maintainAspectRatio = true;
  @Input() aspectRatio = 1;

  @HostBinding('style.width') width: string;
  @HostBinding('style.height') height: string;

  @Output() imageLoaded = new EventEmitter<Dimensions>();
  @Output() loadImageFailed = new EventEmitter<string>();

  reset() {
    this.imageVisible = false;
    this.originalImage = null;
    this.imgDataUrl = 'data:image/png;base64,iVBORw0KGg'
      + 'oAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQYV2NgAAIAAAU'
      + 'AAarVyFEAAAAASUVORK5CYII=';
    this.moveStart = {
      active: false,
      type: null,
      position: null,
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      clientX: 0,
      clientY: 0
    };
    this.maxSize = {
      width: 0,
      height: 0
    };
    this.originalSize = {
      width: 0,
      height: 0
    };
    this.cropper = {
      x1: -100,
      y1: -100,
      x2: 1000,
      y2: 1000
    };
  }

  loadImage(event: any) {
    let file: File;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
      file = event.dataTransfer.files[0];
    } else {
      file = event.target.files[0];
    }

    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (file && imageTypes.indexOf(file.type) !== -1) {
      const fileReader = new FileReader();
      fileReader.onload = (ev: any) => this.loadBase64Image(ev.target.result);
      fileReader.readAsDataURL(file);
    } else {
      this.loadImageFailed.emit('Не удалось загрузить изображение. Убедитесь, что файл ' +
        'является корректным изображением типа JPG, PNG или GIF.');
    }
  }

  private loadBase64Image(imageBase64: string) {
    this.originalImage = new Image();
    this.originalImage.onload = () => {
      this.originalSize.width = this.originalImage.width;
      this.originalSize.height = this.originalImage.height;
      this.updateSize();
    };
    this.imgDataUrl = imageBase64;
    this.originalImage.src = imageBase64;
  }

  imageLoadedInView() {
    if (this.originalImage != null) {
      this.imageLoaded.emit(this.originalSize);
      this.resetCropperPosition();
    }
  }

  private updateSize() {
    const ratio = this.originalSize.width / this.originalSize.height;
    const height = Math.min(this.boundingBox.height, this.originalSize.height);
    this.height = height + 'px';
    this.width = Math.min(this.boundingBox.width, height * ratio) + 'px';
  }

  private resetCropperPosition() {
    const displayedImage = this.elementRef.nativeElement.querySelector('.source-image');
    if (displayedImage.offsetWidth / this.aspectRatio < displayedImage.offsetHeight) {
      this.cropper.x1 = 0;
      this.cropper.x2 = displayedImage.offsetWidth;
      const cropperHeight = displayedImage.offsetWidth / this.aspectRatio;
      this.cropper.y1 = (displayedImage.offsetHeight - cropperHeight) / 2;
      this.cropper.y2 = this.cropper.y1 + cropperHeight;
    } else {
      this.cropper.y1 = 0;
      this.cropper.y2 = displayedImage.offsetHeight;
      const cropperWidth = displayedImage.offsetHeight * this.aspectRatio;
      this.cropper.x1 = (displayedImage.offsetWidth - cropperWidth) / 2;
      this.cropper.x2 = this.cropper.x1 + cropperWidth;
    }
    this.imageVisible = true;
  }

  startMove(event: any, moveType: string, position: string | null = null) {
    this.moveStart.active = true;
    this.moveStart.type = moveType;
    this.moveStart.position = position;
    this.moveStart.clientX = event.clientX || event.touches[0].clientX;
    this.moveStart.clientY = event.clientY || event.touches[0].clientY;
    Object.assign(this.moveStart, this.cropper);
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  moveImg(event: any) {
    if (this.moveStart.active) {
      event.stopPropagation();
      event.preventDefault();
      const el = this.elementRef.nativeElement.querySelector('.source-image');
      this.maxSize.width = el.offsetWidth;
      this.maxSize.height = el.offsetHeight;
      if (this.moveStart.type === 'move') {
        this.move(event);
        this.checkCoordinates(true);
      } else if (this.moveStart.type === 'resize') {
        this.resize(event);
        this.checkCoordinates(false);
      }
    }
  }

  private checkCoordinates(maintainSize = false) {
    if (this.cropper.x1 < 0) {
      this.cropper.x2 -= maintainSize ? this.cropper.x1 : 0;
      this.cropper.x1 = 0;
    }
    if (this.cropper.y1 < 0) {
      this.cropper.y2 -= maintainSize ? this.cropper.y1 : 0;
      this.cropper.y1 = 0;
    }
    if (this.cropper.x2 > this.maxSize.width) {
      this.cropper.x1 -= maintainSize ? (this.cropper.x2 - this.maxSize.width) : 0;
      this.cropper.x2 = this.maxSize.width;
    }
    if (this.cropper.y2 > this.maxSize.height) {
      this.cropper.y1 -= maintainSize ? (this.cropper.y2 - this.maxSize.height) : 0;
      this.cropper.y2 = this.maxSize.height;
    }
  }

  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  moveStop() {
    if (this.moveStart.active) {
      this.moveStart.active = false;
    }
  }

  private move(event: any) {
    const diffX = this.getClientX(event) - this.moveStart.clientX;
    const diffY = this.getClientY(event) - this.moveStart.clientY;

    this.cropper.x1 = this.moveStart.x1 + diffX;
    this.cropper.y1 = this.moveStart.y1 + diffY;
    this.cropper.x2 = this.moveStart.x2 + diffX;
    this.cropper.y2 = this.moveStart.y2 + diffY;
  }

  private resize(event: any) {
    const diffX = this.getClientX(event) - this.moveStart.clientX;
    const diffY = this.getClientY(event) - this.moveStart.clientY;
    switch (this.moveStart.position) {
      case 'left':
        this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
        break;
      case 'topleft':
        this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
        this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
        break;
      case 'top':
        this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
        break;
      case 'topright':
        this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
        this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
        break;
      case 'right':
        this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
        break;
      case 'bottomright':
        this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
        this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
        break;
      case 'bottom':
        this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
        break;
      case 'bottomleft':
        this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
        this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
        break;
    }

    if (this.maintainAspectRatio) {
      this.checkAspectRatio();
    }
  }

  private checkAspectRatio() {
    let overflowX = 0;
    let overflowY = 0;

    switch (this.moveStart.position) {
      case 'top':
        this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
        overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
        overflowY = Math.max(0 - this.cropper.y1, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
        }
        break;
      case 'bottom':
        this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
        overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
        overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : (overflowX / this.aspectRatio);
        }
        break;
      case 'topleft':
        this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
        overflowX = Math.max(0 - this.cropper.x1, 0);
        overflowY = Math.max(0 - this.cropper.y1, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x1 += (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
        }
        break;
      case 'topright':
        this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
        overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
        overflowY = Math.max(0 - this.cropper.y1, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
        }
        break;
      case 'right':
      case 'bottomright':
        this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
        overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
        overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
        }
        break;
      case 'left':
      case 'bottomleft':
        this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
        overflowX = Math.max(0 - this.cropper.x1, 0);
        overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
        if (overflowX > 0 || overflowY > 0) {
          this.cropper.x1 += (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
          this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
        }
        break;
    }
  }

  getCroppedImage(): Promise<Blob> {
    const displayedImage = this.elementRef.nativeElement.querySelector('.source-image');
    if (displayedImage && this.originalImage) {
      const ratio = this.originalSize.width / displayedImage.offsetWidth;
      const left = Math.round(this.cropper.x1 * ratio);
      const top = Math.round(this.cropper.y1 * ratio);
      const width = Math.round((this.cropper.x2 - this.cropper.x1) * ratio);
      const height = Math.round((this.cropper.y2 - this.cropper.y1) * ratio);
      const resizeRatio = Math.min(width, 1280) / width;
      const cropCanvas = <HTMLCanvasElement>document.createElement('canvas');
      cropCanvas.width = width * resizeRatio;
      cropCanvas.height = height * resizeRatio;
      const ctx = cropCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(this.originalImage, left, top, width, height, 0, 0, width * resizeRatio, height * resizeRatio);
        return new Promise(f => {
          cropCanvas.toBlob(f, 'image/jpeg');
        });
      }
    }
  }

  private getClientX(event: any) {
    return event.clientX != null ? event.clientX : event.touches[0].clientX;
  }

  private getClientY(event: any) {
    return event.clientY != null ? event.clientY : event.touches[0].clientY;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/image-cropper/image-cropper.component.ts